import { normalizeCliUserCode } from './state'

export const CLI_AUTH_USER_CODE_STORAGE_KEY = 'opencreator:cli-auth:user-code:v1'

let capturedUserCode: string | null = null

export function isCliAuthorizationPath(pathname: string): boolean {
  return /^\/(?:en\/|zh\/)?cli\/authorize\/?$/.test(pathname)
}

export function readCliAuthorizationUserCodeFromHash(hash: string): string | null {
  if (!hash) return null
  const fragment = new URLSearchParams(hash.replace(/^#/, ''))
  return normalizeCliUserCode(fragment.get('user_code'))
}

export function persistCliAuthorizationUserCode(value: string): string | null {
  const normalized = normalizeCliUserCode(value)
  if (!normalized) return null

  capturedUserCode = normalized
  try {
    window.sessionStorage.setItem(CLI_AUTH_USER_CODE_STORAGE_KEY, normalized)
  } catch {
    // Keep the in-memory value when sessionStorage is unavailable.
  }
  return normalized
}

export function readCliAuthorizationUserCode(): string | null {
  if (capturedUserCode) return capturedUserCode
  try {
    return normalizeCliUserCode(window.sessionStorage.getItem(CLI_AUTH_USER_CODE_STORAGE_KEY))
  } catch {
    return null
  }
}

export function clearCliAuthorizationUserCode(): void {
  capturedUserCode = null
  try {
    window.sessionStorage.removeItem(CLI_AUTH_USER_CODE_STORAGE_KEY)
  } catch {
    // Ignore storage failures.
  }
}

/**
 * Capture the short-lived user code before React, Clerk, or analytics initialize.
 * The CLI credential and device_code are never accepted here.
 */
export function captureCliAuthorizationFragment(): void {
  if (typeof window === 'undefined' || !isCliAuthorizationPath(window.location.pathname)) return
  if (!window.location.hash) return

  const fragment = new URLSearchParams(window.location.hash.slice(1))
  if (fragment.has('user_code')) {
    clearCliAuthorizationUserCode()
    const userCode = readCliAuthorizationUserCodeFromHash(window.location.hash)
    if (userCode) persistCliAuthorizationUserCode(userCode)
  }

  // Clear the entire fragment even when it contains an unsupported credential-like key.
  // Only a valid user_code is ever retained, and only in this tab's sessionStorage.
  const cleanUrl = `${window.location.pathname}${window.location.search}`
  window.history.replaceState(window.history.state, '', cleanUrl)
}
